import React, { useMemo } from 'react';
import { useLocation, Outlet } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { SidebarStateProvider } from './dashboard/nav/SidebarStateContext';
import { AICopilotProvider } from '@/hooks/useAICopilot';
import AppLayout from './layouts/AppLayout';
import MainContent from './MainContent';
import AuthLoadingIndicator from './layouts/AuthLoadingIndicator';
import ErrorFallback from './layouts/ErrorFallback';
import PricingPageBypass from './layouts/PricingPageBypass';
// import SignInDialog from './auth/SignInDialog'; // Commented out unavailable import

interface DashboardLayoutProps {
  children?: React.ReactNode;
}

// Maximum time to show loading indicator before proceeding anyway
const MAX_LOADING_TIME = 2500; // 2.5 seconds

const DashboardLayout: React.FC<DashboardLayoutProps> = ({ children }) => {
  // Use location for debugging
  const location = useLocation();
  const loadTimestamp = useMemo(() => Date.now(), []);
  
  // Check for pricing page - complete bypass
  const pricingBypass = <PricingPageBypass />;
  if (pricingBypass.props.children === null) {
    return null;
  }
  
  // Try to access auth context safely
  try {
    const { isLoading } = useAuth();
    
    // IMPORTANT: Limit loading spinner display to prevent blank screen lockups
    const shouldShowLoading = isLoading && (Date.now() - loadTimestamp < MAX_LOADING_TIME);
    
    if (shouldShowLoading) {
      return (
        <div>
          <div id="dashboard-auth-loading" className="sr-only" data-timestamp={Date.now()}>
            Auth loading
          </div>
          <AuthLoadingIndicator />
        </div>
      );
    }

    // Wrapping with AICopilotProvider without auto-opening it
    return (
      <AICopilotProvider>
        <SidebarStateProvider>
          <AppLayout>
            <MainContent>
              <div id="dashboard-content-wrapper" data-timestamp={Date.now()} data-path={location.pathname}>
                {children || <Outlet />}
              </div>
            </MainContent>
          </AppLayout>
        </SidebarStateProvider>
      </AICopilotProvider>
    );
  } catch (error) {
    // Log the error for debugging
    console.error('🔴 DashboardLayout ERROR:', error);
    
    // Create error tracking in localStorage with limited retention
    try {
      const errorMarker = {
        timestamp: new Date().toISOString(),
        path: location.pathname,
        error: error instanceof Error ? error.message : String(error),
        stack: error instanceof Error ? error.stack?.slice(0, 500) : undefined, // Limit stack size
      };
      
      const errorKey = `dashboard-error-${Date.now()}`;
      localStorage.setItem(errorKey, JSON.stringify(errorMarker));
      
      // Clean up old error entries to prevent storage bloat
      const allKeys = Object.keys(localStorage);
      const errorKeys = allKeys.filter(key => key.startsWith('dashboard-error-')).sort();
      
      // Keep only the last 10 errors
      if (errorKeys.length > 10) {
        errorKeys.slice(0, errorKeys.length - 10).forEach(key => {
          localStorage.removeItem(key);
        });
      }
    } catch (storageError) {
      // Fail silently - don't let storage errors cause more problems
    }
    
    // Graceful fallback when auth context is missing
    return <ErrorFallback error={error instanceof Error ? error : String(error)} />;
  }
};

export default DashboardLayout;
